/*  dead but tested file: Recognized as dead file, but used by go/src/atlas/encrypted_folder_download/service_worker/src/service_worker.ts */

import type {DecryptTransformStreamMetadata} from 'metaserver/static/js/encrypted_folder/download_service_worker/types';
import {decryptChunk} from 'metaserver/static/js/encrypted_folder/content/decrypt';
import {validateAuthSetHmac} from 'metaserver/static/js/encrypted_folder/content/auth';
import type {PerRevisionKeyInfo} from 'metaserver/static/js/encrypted_folder/content/types';
import type {
  Algorithm,
  SymmetricCryptoExtras,
  SymmetricKey,
} from 'metaserver/static/js/encrypted_folder/crypto/types';
import type {FileKey} from 'metaserver/static/js/encrypted_folder/ekms/types';
import {
  captureError,
  SentryError,
} from 'metaserver/static/js/encrypted_folder/download_service_worker/sentry_reporting';
import {decryptPerRevisionKey} from 'metaserver/static/js/encrypted_folder/content/per_revision_key';
import {
  createSymmetricCoreCrypto,
  sha256,
} from 'metaserver/static/js/encrypted_folder/crypto/core_crypto';

if (
  typeof ReadableStream === 'undefined' ||
  ReadableStream.prototype.pipeThrough === undefined ||
  ReadableStream.prototype.pipeTo === undefined ||
  typeof WritableStream === 'undefined' ||
  typeof TransformStream === 'undefined'
) {
  // eslint-disable-next-line dbx/no-sync-require
  require('@stardazed/streams-polyfill');
}

/* global: TransformStream is provided by @stardazed/streams-polyfill */

export const CHUNK_SIZE = 1024 * 1024 * 4; // 4mb (4194304)

export class DecryptTransformStream extends TransformStream<Uint8Array, Uint8Array> {
  private buffer: Uint8Array<ArrayBuffer> = new Uint8Array();
  private chunkIndex: number = 0;

  private readonly chunkSize: number;
  private plaintextBlocklist: Uint8Array;

  private readonly fileKey: FileKey;
  private readonly algorithm: Algorithm;
  private readonly symmetricCryptoExtras: SymmetricCryptoExtras[];
  private readonly perRevisionKeyInfo: PerRevisionKeyInfo;

  private constructor(
    metadata: DecryptTransformStreamMetadata,
    chunkSize: number,
    plaintextBlocklist: Uint8Array
  ) {
    super({
      transform: (chunk: Uint8Array, controller: TransformStreamDefaultController<Uint8Array>) =>
        this.transformData(chunk, controller),
      flush: (controller: TransformStreamDefaultController<Uint8Array>) =>
        this.flushData(controller),
    });

    if (metadata.contentEncryptionExtras.schemeType.case !== 'blockwise') {
      throw new SentryError('Currently only blockwise decryption is supported', {
        tags: {
          location: 'DecryptTransformStream.constructor',
        },
        extras: {
          'contentEncryptionExtras.schemeType': metadata.contentEncryptionExtras.schemeType.case,
        },
      });
    }

    this.chunkSize = chunkSize;

    this.fileKey = metadata.fileKey;
    this.algorithm = metadata.algorithm;
    this.symmetricCryptoExtras =
      metadata.contentEncryptionExtras.schemeType.value.blockEncryptionExtras;
    this.perRevisionKeyInfo = metadata.perRevisionKeyInfo;
    this.plaintextBlocklist = plaintextBlocklist;
  }

  static async validateAuthSetHmacAndCreateStream(
    metadata: DecryptTransformStreamMetadata,
    chunkSize: number = CHUNK_SIZE
  ): Promise<DecryptTransformStream> {
    const schemeType = metadata.contentEncryptionExtras.schemeType;
    if (schemeType.case !== 'blockwise' || !schemeType.value) {
      throw new SentryError('Currently only blockwise decryption is supported', {
        tags: {
          location: 'DecryptTransformStream.validateAuthSetHmacAndCreateStream',
        },
        extras: {
          schemeType: schemeType.case,
          is_nil: !schemeType.value,
        },
      });
    }

    try {
      await validateAuthSetHmac(
        metadata.fileKey,
        metadata.algorithm,
        metadata.perRevisionKeyInfo,
        schemeType.value
      );

      const perRevisionKey = await decryptPerRevisionKey(
        metadata.perRevisionKeyInfo,
        metadata.algorithm,
        metadata.fileKey
      );
      const plaintextBlocklist = await DecryptTransformStream.decryptBlocklist(
        metadata.encryptedPlaintextBlocklist,
        metadata.plaintextBlocklistEncryptionExtras,
        perRevisionKey
      );

      return new DecryptTransformStream(metadata, chunkSize, plaintextBlocklist);
    } catch (e) {
      throw new SentryError(e, {
        tags: {
          location: 'DecryptTransformStream.validateAuthSetHmacAndCreateStream',
        },
        extras: {
          algorithm: metadata.algorithm,
          schemeType: schemeType.case,
        },
      });
    }
  }

  private async transformData(
    chunk: Uint8Array,
    controller: TransformStreamDefaultController<Uint8Array>
  ) {
    try {
      // Append the incoming chunk to the buffer
      this.buffer = new Uint8Array([...this.buffer, ...new Uint8Array(chunk)]);

      // Check if the buffer has enough data for at least one complete chunk
      while (this.buffer.length >= this.chunkSize) {
        const chunkData = this.buffer.slice(0, this.chunkSize);

        const currentExtras = this.symmetricCryptoExtras[this.chunkIndex];
        const decryptedData = await decryptChunk(
          chunkData,
          this.fileKey,
          this.algorithm,
          this.perRevisionKeyInfo,
          currentExtras
        );

        await this.verifyDecryptedBlock(decryptedData);
        controller.enqueue(decryptedData);

        // Remove the processed chunk from the buffer
        this.buffer = this.buffer.slice(this.chunkSize);
        this.chunkIndex++;
      }
    } catch (error) {
      captureError(
        new SentryError(error, {
          tags: {location: 'DecryptTransformStream.transformData'},
        })
      );
      controller.error(error);
    }
  }

  private async flushData(controller: TransformStreamDefaultController<Uint8Array>) {
    try {
      // Handle any remaining data in the buffer when the stream ends
      if (this.buffer.length > 0) {
        const currentExtras = this.symmetricCryptoExtras[this.chunkIndex];
        const decryptedData = await decryptChunk(
          this.buffer,
          this.fileKey,
          this.algorithm,
          this.perRevisionKeyInfo,
          currentExtras
        );

        await this.verifyDecryptedBlock(decryptedData);
        controller.enqueue(decryptedData);
        this.chunkIndex++;
      }

      if (this.symmetricCryptoExtras.length !== this.chunkIndex) {
        throw new SentryError('Integrity error: Invalid block count', {
          extras: {
            'symmetricCryptoExtras.length': this.symmetricCryptoExtras.length,
            chunkIndex: this.chunkIndex,
          },
        });
      }
    } catch (err) {
      captureError(
        new SentryError(err, {
          tags: {location: 'DecryptTransformStream.flushData'},
        })
      );
      controller.error(err);
    }
  }

  private async verifyDecryptedBlock(decryptedBlock: Uint8Array<ArrayBuffer>) {
    const plaintextHash = await sha256(decryptedBlock);

    // verify that the plaintextHash is a prefix of this.plaintextBlocklist
    if (plaintextHash.length > this.plaintextBlocklist.length) {
      throw new Error('Plaintext does not match expectation');
    }
    for (let i = 0; i < plaintextHash.length; i++) {
      if (plaintextHash[i] !== this.plaintextBlocklist[i]) {
        throw new Error('Plaintext does not match expectation');
      }
    }
    this.plaintextBlocklist = this.plaintextBlocklist.slice(plaintextHash.length);
  }

  private static async decryptBlocklist(
    encryptedBlocklist: Uint8Array<ArrayBuffer>,
    serializedEncryptionExtras: Uint8Array,
    perRevisionKey: SymmetricKey
  ) {
    const symmetricCrypto = createSymmetricCoreCrypto(perRevisionKey.algorithm);
    const extras = symmetricCrypto.deserializeCryptoExtras(serializedEncryptionExtras);
    return symmetricCrypto.decrypt(encryptedBlocklist, perRevisionKey, extras);
  }

  get readableWritablePair(): ReadableWritablePair {
    return {
      readable: this.readable,
      writable: this.writable,
    };
  }
}
